/**
 * Die Karte „Selbstverbesserung“ auf der Seite „Erweitert“. (17.10.2026, Phase 1 „Vorschlagen“, Plan genehmigt 05:17)
 *
 * Oben der Schalter „Vorschläge sammeln“ (Kontowert `selbstverbesserung`: "aus" | "vorschlagen", Vorgabe aus). Darunter die
 * Vorschläge der Nacht (GET /api/selbstverbesserung): je Vorschlag Titel, ein Satz, Klasse/Aufwand/Nutzen/Beleg und die Knöpfe
 * Ja / Nein / Später, soweit der Wechsel erlaubt ist (ein Ja lässt sich mit Nein/Später zurücknehmen) — jeder mit Zeichen UND Wort, Ziel ≥ 44 pt. Ein „Ja“ baut nichts: der Schmied greift es auf.
 *
 * ⚠️ Nur Konto 1 (shared/selbstverbesserung.ts) — sonst `null`, kein Abruf.
 * ⚠️ Der Schalter ist „schutz“ (shared/einstellung-aendern.ts): nur Martin selbst, nie auf Zuruf.
 * ⚠️ Kein Takt: ein Abruf beim Bauen und nach jeder Antwort. Fällt er aus, steht ein Satz da — nie ein Fehlerbild.
 */
import {
  SELBSTVERBESSERUNG_FELD,
  eintragAus,
  selbstverbesserungFuerKonto,
  selbstverbesserungModus,
  wechselErlaubt,
  type Antwort,
  type Marke,
  type SelbstverbesserungEintrag,
  type Status,
} from "../../../shared/selbstverbesserung.js";
import { einstAbschnitt, einstSchalter } from "./einst-bausteine.js";

export interface SelbstverbesserungKontext {
  uid: number | null | undefined;
  werte: () => Record<string, unknown>;
  speichere: (felder: Record<string, unknown>, satz: string) => void;
  /** Für Tests ersetzbar. `null`: nicht abrufbar. */
  hole?: () => Promise<SelbstverbesserungEintrag[] | null>;
  /** Für Tests ersetzbar. `null`: abgelehnt oder nicht erreichbar. */
  antworte?: (id: number, a: Antwort) => Promise<SelbstverbesserungEintrag | null>;
}

const PFAD = "/api/selbstverbesserung";

/** Streng lesen. Rein. */
export function listeAus(roh: unknown): SelbstverbesserungEintrag[] | null {
  if (!roh || typeof roh !== "object" || (roh as { ok?: unknown }).ok !== true) return null;
  const e = (roh as { eintraege?: unknown }).eintraege;
  if (!Array.isArray(e)) return null;
  return e.map(eintragAus).filter((x): x is SelbstverbesserungEintrag => x !== null).slice(0, 30);
}

async function holeVomServer(): Promise<SelbstverbesserungEintrag[] | null> {
  try {
    const r = await fetch(PFAD, { credentials: "same-origin", cache: "no-store" });
    return r.ok ? listeAus(await r.json()) : null;
  } catch {
    return null;
  }
}

async function antworteServer(id: number, antwort: Antwort): Promise<SelbstverbesserungEintrag | null> {
  try {
    const r = await fetch(`${PFAD}/${id}/antwort`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ antwort }),
    });
    if (!r.ok) return null;
    const j = (await r.json()) as { ok?: unknown; eintrag?: unknown };
    return j.ok === true ? eintragAus(j.eintrag) : null;
  } catch {
    return null;
  }
}

/** Die Knöpfe: Zeichen und Wort. */
export const ANTWORT_KNOEPFE: ReadonlyArray<{ antwort: Antwort; zeichen: string; wort: string }> = [
  { antwort: "ja", zeichen: "✓", wort: "Ja" },
  { antwort: "nein", zeichen: "✕", wort: "Nein" },
  { antwort: "spaeter", zeichen: "↻", wort: "Später" },
];

/** Der Status in Worten. Rein. */
export function statusWort(s: Status): string {
  return s === "vorschlag" ? "offen" : s === "ja" ? "Ja — wartet auf den Schmied" : s === "nein" ? "Nein" : s === "spaeter" ? "Später" : "gebaut";
}

/** Die mitlaufende Marke in Worten (der Server rechnet sie aus dem Ticketstatus). Rein. */
export function markeWort(m: Marke, ticketId: number | null): string {
  const nr = ticketId === null ? "" : ` (Ticket ${ticketId})`;
  switch (m) {
    case "erfasst": return `vom Schmied erfasst${nr}`;
    case "in_arbeit": return `in Arbeit${nr}`;
    case "gebaut": return `gebaut${nr}`;
    case "zurueckgestellt": return `zurückgestellt${nr}`;
    case "braucht_ja": return "braucht dein Ja";
  }
}

/** Die leise Zeile unter dem Satz. Rein. */
export function metaZeile(e: SelbstverbesserungEintrag): string {
  const zahl = e.belegZahl === null ? "" : ` (${e.belegZahl})`;
  return `Klasse ${e.klasse} · Aufwand ${e.aufwand} · Nutzen ${e.nutzen}/5 · Beleg ${e.beleg}${zahl}`;
}

function eintragDom(e: SelbstverbesserungEintrag, k: SelbstverbesserungKontext, neu: () => void): HTMLElement {
  const li = document.createElement("li");
  li.className = "selbstv-eintrag";
  li.dataset.id = String(e.id);
  li.dataset.status = e.status;
  const titel = document.createElement("span");
  titel.className = "selbstv-titel";
  titel.textContent = e.titel;
  const satz = document.createElement("span");
  satz.className = "selbstv-satz";
  satz.textContent = e.beschreibung;
  const meta = document.createElement("span");
  meta.className = "selbstv-meta";
  meta.textContent = metaZeile(e);
  const stand = document.createElement("span");
  stand.className = "selbstv-stand";
  stand.setAttribute("role", "status");
  stand.textContent = `Stand: ${statusWort(e.status)}`;
  li.append(titel, satz, meta, stand);
  if (e.marke) {
    const marke = document.createElement("span");
    marke.className = "selbstv-marke";
    marke.dataset.marke = e.marke;
    marke.textContent = markeWort(e.marke, e.ticketId);
    li.appendChild(marke);
  }
  // Ein Ja ist zurücknehmbar (Nein / Später), solange es nicht gebaut ist — shared/selbstverbesserung.ts `wechselErlaubt`.
  if (e.status === "vorschlag" || e.status === "spaeter" || e.status === "ja") {
    const knoepfe = document.createElement("div");
    knoepfe.className = "selbstv-knoepfe";
    knoepfe.setAttribute("role", "group");
    knoepfe.setAttribute("aria-label", `Antwort zu ${e.titel}`);
    for (const b of ANTWORT_KNOEPFE) {
      if (!wechselErlaubt(e.status, b.antwort)) continue;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "selbstv-knopf";
      btn.dataset.antwort = b.antwort;
      const z = document.createElement("span");
      z.className = "selbstv-knopf-zeichen";
      z.setAttribute("aria-hidden", "true");
      z.textContent = b.zeichen;
      const w = document.createElement("span");
      w.textContent = b.wort;
      btn.append(z, w);
      btn.addEventListener("click", () => {
        knoepfe.querySelectorAll("button").forEach((x) => (x.disabled = true));
        stand.textContent = "Wird gespeichert …";
        void (k.antworte ?? antworteServer)(e.id, b.antwort)
          .catch(() => null)
          .then((r) => {
            if (!r) {
              stand.textContent = "Nicht gespeichert — bitte gleich noch einmal.";
              knoepfe.querySelectorAll("button").forEach((x) => (x.disabled = false));
              return;
            }
            neu();
          });
      });
      knoepfe.appendChild(btn);
    }
    li.appendChild(knoepfe);
  }
  return li;
}

/** Die Karte. Nur Konto 1: sonst `null`. */
export function baueSelbstverbesserungKarte(k: SelbstverbesserungKontext): HTMLElement | null {
  if (!selbstverbesserungFuerKonto(k.uid)) return null;
  const a = einstAbschnitt("Selbstverbesserung");
  const an = selbstverbesserungModus(k.werte()[SELBSTVERBESSERUNG_FELD], k.uid) === "vorschlagen";
  a.karte.appendChild(
    einstSchalter(
      "Vorschläge sammeln",
      "Jede Nacht höchstens drei Vorschläge aus Kennzahlen und Tickets. Gebaut wird nur nach deinem Ja.",
      an,
      (neu) => {
        k.speichere(
          { [SELBSTVERBESSERUNG_FELD]: neu ? "vorschlagen" : "aus" },
          neu ? "Vespera sammelt ab heute Nacht Vorschläge." : "Es werden keine Vorschläge mehr gesammelt.",
        );
      },
      SELBSTVERBESSERUNG_FELD,
    ).zeile,
  );
  const block = document.createElement("div");
  block.className = "einst-feld-block selbstv-block";
  const satz = document.createElement("p");
  satz.className = "einst-feld-satz selbstv-leer";
  satz.setAttribute("role", "status");
  satz.setAttribute("aria-live", "polite");
  const liste = document.createElement("ul");
  liste.className = "selbstv-liste";
  liste.setAttribute("aria-label", "Vorschläge");
  block.append(satz, liste);
  a.karte.appendChild(block);

  const lade = (): void => {
    satz.textContent = "Vorschläge werden geladen …";
    void (k.hole ?? holeVomServer)()
      .catch(() => null)
      .then((eintraege) => {
        if (!eintraege) {
          satz.textContent = "Die Vorschläge sind gerade nicht abrufbar.";
          liste.replaceChildren();
          return;
        }
        satz.textContent = eintraege.length ? "" : "Keine offenen Vorschläge.";
        satz.hidden = eintraege.length > 0;
        liste.replaceChildren(...eintraege.map((e) => eintragDom(e, k, lade)));
      });
  };
  lade();
  return a.abschnitt;
}
